"use strict";

class MapClass {
  constructor() {
    this.map = null;
    this.defualt_latitude = 32.804192;
    this.defualt_longitude = 35.17178;
    this.marker = null; // Add a marker property to store the marker
    this.onMarkerChangeCallback = null;
  }

  // Setter for latitude and longitude
  setLatLng(lat, lng) {
    this.default_latitude = lat;
    this.default_longitude = lng;

    if (this.marker) {
      this.marker.setLatLng([lat, lng]);
    }
  }

  getLatLng() {
    return {
      latitude: this.default_latitude,
      longitude: this.default_longitude,
    };
  }

  // Initialize the map in the specified HTML element
  async initMap(element, options = {}) {
    if (!element) {
      return;
    }
    
    try {
      const defaultOptions = {
        center: { lat: this.defualt_latitude, lng: this.defualt_longitude },
        zoom: 10,
        mapId: "PLACES",
      };

      const mapOptions = { ...defaultOptions, ...options };
      // Initialize the map
      this.map = new google.maps.Map(element, mapOptions);

      // Add click event listener to the map
      this.map.addListener("click", (e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        this.updateMapCenter(lat, lng);
      });
    } catch (error) {
      console.error("Error initializing map:", error);
    }
  }

  // Get latitude and longitude using the Google Maps Geocoding API
  async getLatLngByCityName(cityName) {
    if (!cityName) {
      console.error("City name is required for geocoding.");
      return;
    }

    try {
      const geocoder = new google.maps.Geocoder();
      const results = await new Promise((resolve, reject) => {
        geocoder.geocode({ address: cityName }, (results, status) => {
          if (status === "OK") {
            resolve(results[0].geometry.location);
          } else {
            reject(`Geocode failed: ${status}`);
          }
        });
      });

      return {
        lat: results.lat(),
        lng: results.lng(),
      };
    } catch (error) {
      return null;
    }
  }

  // Setup autocomplete functionality for an input field
  setupAutocomplete(inputElement, callback) {
    try {
      if (!inputElement) {
        return;
      }

      const autocomplete = new google.maps.places.Autocomplete(inputElement, {
        types: ["geocode"],
      });

      // Listen for the 'place_changed' event
      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (!place.geometry) {
          return;
        }

        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        // Extract and log the city name
        let cityName = "";
        for (let i = 0; i < place.address_components.length; i++) {
          const component = place.address_components[i];
          if (component.types.includes("locality")) {
            cityName = component.long_name;
            break;
          }
        }

        this.updateMapCenter(lat, lng);
        if (callback && typeof callback === "function") {
          const locationData = {
            lat: lat,
            lng: lng,
            city: cityName,
          };

          callback(locationData);
        }
      });
    } catch (error) {}
  }

  // Set or update the marker on the map
  async setOrUpdateMarker(lat, lng) {
    if (this.map) {
      const position = new google.maps.LatLng(lat, lng);
      if (this.marker) {
        this.marker.position = position;
      } else {
        this.marker = new google.maps.marker.AdvancedMarkerElement({
          map: this.map,
          position: position,
          title: "",
        });
      }

      // Notify listener if any
      if (this.onMarkerChangeCallback) {
        // Get address details from lat,lng
        let addressData = null;
        try {
          addressData = await this.formattedAddress(lat, lng);
        } catch (error) {}
        this.onMarkerChangeCallback({
          lat: lat,
          lng: lng,
          address: addressData,
        });
      }
    }
  }

  updateMapCenter(lat, lng) {
    if (this.map) {
      const newCenter = new google.maps.LatLng(lat, lng);
      this.map.setCenter(newCenter);
      this.setOrUpdateMarker(lat, lng);
    }
  }

  async onMarkerChange(callback) {
    if (typeof callback === "function") {
      this.onMarkerChangeCallback = callback;
    }
  }

  async reverseGeocode(lat, lng) {
    if (!this.map) return null;
    const geocoder = new google.maps.Geocoder();

    try {
      const results = await new Promise((resolve, reject) => {
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === "OK") {
            resolve(results);
          } else {
            reject(status);
          }
        });
      });

      if (results && results.length > 0) {
        return results[0];
      }
      return null;
    } catch {
      return null;
    }
  }

  async formattedAddress(lat, lng) {
    return new Promise(async (resolve, reject) => {
      if (!lat || !lng) {
        return reject("Latitude and Longitude are required for reverse geocoding.");
      }
      try {
        const result = await this.reverseGeocode(lat, lng);
        if (!result) return reject("No result found for the given coordinates.");

        // Parse address components
        const components = result.address_components;
        const getComponent = (type) => {
          const comp = components.find((c) => c.types.includes(type));
          return comp ? comp.long_name : "";
        };

        resolve({
          formatted_address: result.formatted_address,
          street: getComponent("establishment") || getComponent("route") || getComponent("point_of_interest") || getComponent("transit_station"),
          street_number: getComponent("street_number"),
          city: getComponent("locality") || getComponent("administrative_area_level_2"),
          state: getComponent("administrative_area_level_1"),
          country: getComponent("country"),
          postal_code: getComponent("postal_code"),
        });
      } catch (error) {
        reject(`Error reversing address: ${error}`);
      }
    });
  }
}

const $MapClass = new MapClass();
